import React, { memo, useRef, useEffect } from 'react'
import { debounce, keys, last } from 'lodash'
import PropTypes from 'prop-types'
import cn from 'classnames'

import styles from './Item.module.scss'

import { addPaddingAndScrollUpToElement, getElementScrollPosition, addExtraPadding, ScrollUpToElement, scrollingHasStopped } from '../utils/search'

const Item = ({ id, name, price, categoryId, emojis, status, maxReached, inventory, cartQuantity, color, onTap, className, highlighted, focusedItem, categoryBreakPoints, scrollPositions, setScrollPositions, selectedCategory, setSelectedCategory }) => {
  const available = status === 'available'
  let quantityElement = <span className={styles.add}>+</span>
  const itemRef = useRef();
  let classNames = cn(styles.item, className)
  let style = { backgroundColor: color, color }
  let _price = price
  let _onTap = onTap
  const previousCartQuantityRef = useRef()

  if (inventory <= 10) {
    price = `${price} - ${inventory} LEFT!`
  }

  if (cartQuantity > 0) {
    quantityElement = <span className={styles.quantity}>{cartQuantity}</span>
  }

  if (maxReached) {
    style.opacity = 0.6
    _price = 'MAX REACHED'
    _onTap = () => {}
  }

  if (!available) {
    classNames = cn(classNames, styles.unavailable)
    style = {}
    _price = 'SOLD OUT'
    _onTap = () => {}
  }

  if (!highlighted) {
    style.opacity = ".1"
  }

  if (focusedItem) {
    classNames = cn(classNames, styles.active)
  }

  useEffect(() => {
    if (focusedItem && itemRef.current) {
      let setIntervalTimer
      if (!selectedCategory.search) {
        addPaddingAndScrollUpToElement(itemRef.current)
        setIntervalTimer = 10
      }
      if (categoryBreakPoints[categoryId] === id && selectedCategory.search) {
        ScrollUpToElement(itemRef.current)
        setIntervalTimer = 100
      }  
      const scrollTimer = setInterval(() => {
        if(!itemRef.current || scrollingHasStopped(itemRef.current)) {
          setSelectedCategory({ id: categoryId, search: false, highlight: true })
          clearInterval(scrollTimer)
        }
      }, setIntervalTimer)
      setTimeout(() => {
        clearInterval(scrollTimer)
      }, 1000)
    }
  }, [focusedItem])

  useEffect(() => {
    if ( itemRef.current && !scrollPositions[categoryId] && categoryBreakPoints[categoryId] === id) {
      const scrollPosition = getElementScrollPosition(itemRef.current)
      setScrollPositions({ [categoryId]: scrollPosition, ...scrollPositions })
      
      if (categoryBreakPoints[last(keys(categoryBreakPoints))] === id) {
        addExtraPadding(itemRef.current)
      }
    }
  }, [categoryBreakPoints, scrollPositions])

  // Ensure that no request is made to increase cart quantity until last request has finished.
  const handleOnTap = () => {
    if (cartQuantity === 0 || cartQuantity !== previousCartQuantityRef?.current) {
      previousCartQuantityRef.current = cartQuantity
      _onTap()
    }
  }

  return (
    <div className={classNames} ref={itemRef} style={style} onClick={debounce(handleOnTap, 50)}>
      <div className={styles.title}>
        <span className={styles.name}>{name}</span>
      </div>
      <div className={styles.details}>
        <div className={styles.inventoryPrice}>
          <span className={styles.price}>{_price}</span>
        </div>
        {quantityElement}
      </div>
    </div>
  )
}

Item.defaultProps = {
  status: 'available',
  maxReached: false,
  emojis: [],
}

Item.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.string,
  emojis: PropTypes.array,
  status: PropTypes.oneOf(['unavailable', 'available']),
  maxReached: PropTypes.bool,
  inventory: PropTypes.number,
  cartQuantity: PropTypes.number,
  color: PropTypes.string,
  onTap: PropTypes.func,
  className: PropTypes.string,
  highlighted: PropTypes.bool,
  focusedItem: PropTypes.bool
}

export default memo(Item)
